/* eslint-disable lines-between-class-members */
import {
  ScriptableObject,
  ScriptableObjectMixin,
  ScriptableObjects
} from "./Base.js";

const BOX = "box";
const MANUAL = "manual";

const BOX_COLLIDER_PROPS = ["walkable"];

// --------------------------------------------------------------------------
// A-Frame adapter
// --------------------------------------------------------------------------

// Adapter that encapsulates all interactions with the underlying A-Frame
// entity. It mirrors component/attribute values and supports "pending"
// updates for cases where the component is not yet initialised on the
// element when a property is assigned from JS.
class BoxColliderAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    // Store updates performed before the component is ready
    const _pendingUpdates = {};
    this._pendingUpdates = _pendingUpdates;

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;

      // Flush box-collider component updates
      if (_pendingUpdates["shape-helper"]) {
        const current = {
          ...(this.sourceObject.el.getAttribute("shape-helper") || {})
        };
        Object.assign(current, _pendingUpdates["shape-helper"]);
        this.sourceObject.el.setAttribute("shape-helper", current);
        delete _pendingUpdates["shape-helper"];
      }

      // Flush walkable attribute updates
      if ("walkable" in _pendingUpdates) {
        if (_pendingUpdates.walkable) {
          this.sourceObject.el.setAttribute("walkable", "");
        } else {
          this.sourceObject.el.removeAttribute("walkable");
        }
        delete _pendingUpdates.walkable;
      }
    };

    // Listen for component initialisation so we know when it is safe to
    // apply pending updates.
    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === "shape-helper") {
          _flushPending();
        }
      });

      // Also attempt a first flush immediately – this covers the case where
      // the component was already present or the pending updates concern the
      // walkable attribute which does not depend on any component.
      _flushPending();
    }

    /* ------------------------------------------------------------------ */
    /*  Property definitions                                              */
    /* ------------------------------------------------------------------ */

    // walkable boolean attribute ---------------------------------------
    Object.defineProperty(this, "walkable", {
      get: () => {
        return this.sourceObject?.el?.hasAttribute("walkable") || false;
      },
      set: v => {
        if (!this.sourceObject?.el) {
          _pendingUpdates.walkable = !!v;
          return;
        }

        if (v) {
          this.sourceObject.el.setAttribute("walkable", "");
        } else {
          this.sourceObject.el.removeAttribute("walkable");
        }
      }
    });
  }

  /**
   * Copy box collider properties and children
   */
  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    THREE.Object3D.prototype.copy.call(this, source, false);

    // Copy box collider properties
    BOX_COLLIDER_PROPS.forEach(prop => {
      const val = source[prop];
      if (val === undefined) return;

      // deep-clone where necessary to avoid shared refs
      if (Array.isArray(val)) {
        this[prop] = [...val];
      } else {
        this[prop] = val;
      }
    });

    // Clear existing children
    if (this.children.length) {
      [...this.children].forEach(c => super.remove(c));
    }
    if (this._forwardedChildren?.size) {
      this._forwardedChildren.forEach(c => this.el.object3D.remove(c));
      this._forwardedChildren.clear();
    }

    // Replicate children
    if (recursive) {
      // Scriptable children
      source.children.forEach(childSrc => {
        const child = childSrc.clone(true);
        this.add(child);
      });

      // Forwarded children
      if (source._forwardedChildren?.size) {
        source._forwardedChildren.forEach(fwdSrc => {
          const fwd = fwdSrc.clone(true);
          this.el.object3D.add(fwd);
          this._forwardedChildren.add(fwd);
        });
      }
    }

    return this;
  }
}

// --------------------------------------------------------------------------
// A-Frame wrapper
// --------------------------------------------------------------------------

class AFrameBoxColliderWrapper extends ScriptableObject {
  constructor(el, initial = {}) {
    // THREE.Object3D constructor is called within ScriptableObject so we
    // don’t forward *el* to `super`.
    super();

    this.scriptableType = "box-collider";
    this.el = el;

    // Bridge to A-Frame world
    this._adapter = new BoxColliderAFrameAdapter(this);

    // Apply any initial properties that were provided
    Object.keys(initial).forEach(key => {
      if (key in this) this[key] = initial[key];
    });

    // Read initial transform so position/rotation/scale match the entity
    this.updateMatrixWorld(true);
  }
}

// Expose BOX_COLLIDER_PROPS on the wrapper prototype via the adapter
BOX_COLLIDER_PROPS.forEach(prop => {
  Object.defineProperty(AFrameBoxColliderWrapper.prototype, prop, {
    get() {
      return this._adapter?.[prop];
    },
    set(v) {
      if (this._adapter) {
        this._adapter[prop] = v;
      }
    },
    enumerable: true,
    configurable: true
  });
});

/* -------------------------------------------------------------------------- */
/*                               Editor Augmentation                          */
/* -------------------------------------------------------------------------- */

class BoxColliderEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    BOX_COLLIDER_PROPS.forEach(prop => {
      // Safely attempt to read the initial value without triggering setters
      let initialValue;
      try {
        const descriptor = originalDescriptors.get(prop);
        if (descriptor && descriptor.get) {
          initialValue = descriptor.get.call(sourceObject);
        } else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) {
          initialValue = sourceObject[prop];
        }
      } catch (e) {
        initialValue = undefined;
      }

      this._values.set(prop, initialValue);

      // Define mirrored accessors on *this* so the outer wrapper can proxy
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          // Store internally first
          this._values.set(prop, v);

          // Attempt to propagate to the underlying object via the original
          // descriptor if one existed.
          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            try {
              originalDescriptor.set.call(sourceObject, v);
            } catch (e) {
              console.warn(`Failed to call original setter for ${prop}:`, e);
            }
          } else {
            // Fallback – set the property directly
            try {
              sourceObject[prop] = v;
            } catch (e) {
              /* Ignore unsafe sets */
            }
          }
        }
      });
    });
  }
}

// Helper to walk the prototype chain searching for a property descriptor
function findPropertyDescriptor(obj, prop) {
  let current = obj;
  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, prop);
    if (descriptor) return descriptor;
    current = Object.getPrototypeOf(current);
  }
  return null;
}

function makeEditorBoxColliderNodeScriptable(node) {
  if (node.isScriptableObject) return node; // Already augmented

  const Ctor = node.constructor;
  if (!Ctor) return node;

  // Apply ScriptableObject capabilities to the BoxCollider node
  const ScriptableBoxColliderClass = ScriptableObjectMixin(
    Ctor,
    "BoxColliderElement"
  );
  Object.setPrototypeOf(node, ScriptableBoxColliderClass.prototype);

  // Flag as scriptable
  node.isScriptableObject = true;
  node._transformSyncSetup = false;
  node.scriptableType = "box-collider";

  // Capture original descriptors BEFORE we override them
  const originalDescriptors = new Map();
  BOX_COLLIDER_PROPS.forEach(prop => {
    const descriptor = findPropertyDescriptor(node, prop);
    originalDescriptors.set(prop, descriptor);
  });

  // Create adapter with the captured descriptors
  node._adapter = new BoxColliderEditorAdapter(node, originalDescriptors);

  // Proxy properties through the adapter
  BOX_COLLIDER_PROPS.forEach(prop => {
    Object.defineProperty(node, prop, {
      get() {
        return this._adapter?.[prop];
      },
      set(v) {
        if (this._adapter) {
          this._adapter[prop] = v;
        }
      },
      enumerable: true,
      configurable: true
    });
  });

  // Ensure clones preserve scriptable behaviour
  if (!ScriptableBoxColliderClass.prototype.hasOwnProperty("clone")) {
    const originalClone = Ctor.prototype.clone;
    ScriptableBoxColliderClass.prototype.clone = function(recursive = true) {
      const cloned = originalClone.call(this, recursive);
      return makeEditorBoxColliderNodeScriptable(cloned);
    };
  }

  return node;
}

/* -------------------------------------------------------------------------- */
/*  Convenience element creators                                               */
/* -------------------------------------------------------------------------- */

function _applyInitialProps(instance, initial) {
  Object.keys(initial).forEach(k => {
    const descriptor =
      Object.getOwnPropertyDescriptor(instance, k) ||
      Object.getOwnPropertyDescriptor(Object.getPrototypeOf(instance), k);
    if (descriptor) {
      instance[k] = initial[k];
    }
  });
}

export function createBoxColliderElementAFrame(initial = {}) {
  if (typeof document === "undefined") {
    console.error(
      "Cannot create BoxColliderElementAFrame in non-browser environment"
    );
    return {};
  }

  const el = document.createElement("a-entity");

  const wrapper = new AFrameBoxColliderWrapper(el, initial);

  // Apply any additional initial props that are not handled by the wrapper.
  _applyInitialProps(wrapper, initial);

  // Ensure matrices are up to date before computing collider parameters.
  wrapper.updateMatrixWorld(true);

  // Computes and assigns the shape-helper component so that the collider
  // matches the wrapper's current transform.
  const updateShapeHelper = () => {
    const { position, rotation, scale } = wrapper;
    const orientation = new THREE.Quaternion().setFromEuler(rotation);

    el.setAttribute("shape-helper", {
      type: BOX,
      fit: MANUAL,
      offset: { x: position.x, y: position.y, z: position.z },
      halfExtents: {
        x: scale.x / 2,
        y: scale.y / 2,
        z: scale.z / 2
      },
      orientation: {
        x: orientation.x,
        y: orientation.y,
        z: orientation.z,
        w: orientation.w
      }
    });
  };

  // Initial sync.
  updateShapeHelper();

  // Add clone method to the wrapper
  wrapper.clone = function(recursive = true) {
    const init = {};
    BOX_COLLIDER_PROPS.forEach(p => {
      const v = this[p];
      if (v !== undefined) init[p] = v;
    });

    const cloned = createBoxColliderElementAFrame(init);
    return cloned.copy(this, recursive);
  };

  return wrapper;
}

export function createBoxColliderElementEditor(initial = {}) {
  /* eslint-disable no-undef */
  const BoxColliderCtor =
    (typeof globalThis !== "undefined" &&
      (globalThis.BoxColliderNode || globalThis.BoxCollider)) ||
    undefined;

  if (typeof BoxColliderCtor !== "function") {
    throw new Error(
      "BoxColliderElementEditor: BoxColliderNode constructor not found – cannot create element in editor runtime."
    );
  }

  const editor = ScriptableObject.editor;
  if (!editor) {
    throw new Error(
      "BoxColliderElementEditor: Editor instance not available – cannot create element in editor runtime."
    );
  }

  let node;
  try {
    node = new BoxColliderCtor(editor);
  } catch (e) {
    throw new Error(
      `BoxColliderElementEditor: failed to construct BoxColliderNode – ${e?.message ||
        e}`
    );
  }

  const scriptable = makeEditorBoxColliderNodeScriptable(node);
  _applyInitialProps(scriptable, initial);
  return scriptable;
}

export class BoxColliderElement {
  constructor(initial = {}) {
    return ScriptableObject.runtimeMode === "aframe"
      ? createBoxColliderElementAFrame(initial)
      : createBoxColliderElementEditor(initial);
  }
}

ScriptableObjects.registerType("box-collider", sourceObject => {
  if (ScriptableObject.runtimeMode === "aframe") {
    if (sourceObject.el) {
      const wrapper = new AFrameBoxColliderWrapper(sourceObject.el);

      // Copy children from the source object to the wrapper
      if (sourceObject.children && sourceObject.children.length > 0) {
        // Check if there is another scriptableType children and wrap it
        sourceObject.children.slice().forEach(child => {
          if (child.scriptableType) {
            wrapper.add(ScriptableObjects._wrapObject(child));
          } else {
            // We need to save a reference to the child in the wrapper
          }
        });
      }

      return wrapper;
    }
    return sourceObject;
  }

  return makeEditorBoxColliderNodeScriptable(sourceObject);
});
